import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { Palette, useTheme } from "@mui/material/styles";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { List, Icon, Button, Tooltip, ListItem, ListItemIcon, useMediaQuery, ListItemButton, Box } from "@mui/material";

import { appInsightsTracker } from "utils/AppInsightsManager/appInsights.utils";
import { RESOURCES_KEYS } from "utils/i18n/resources";

import {
  StyledDrawer,
  getSelectedStyle,
  styledNavigation,
  StyledDrawerHeader,
  StyledSwipeableDrawer,
  styledNavigationItemIcon,
} from "./Sidebar.styles";

import Parameters from "assets/images/parameters.svg";
import Favorite from "assets/images/favorite.svg";
import Library from "assets/images/library.svg";
import Logout from "assets/images/logout.svg";
import Home from "assets/images/home.svg";
import Acknowledgement from "assets/images/acknowledgement.svg";

import packageJson from "../../../package.json";
interface ISidebarProps {
  open: boolean;
  drawerwidth: number;
  handleDrawerClose: () => void;
  handleDrawerOpen?: () => void;
}

const Sidebar: React.FunctionComponent<ISidebarProps> = ({
  open,
  drawerwidth,
  handleDrawerOpen,
  handleDrawerClose,
}) => {
  const theme = useTheme();
  const { instance } = useMsal();
  const { pathname } = useLocation();
  const { t: translate } = useTranslation();
  const isAuthenticated = useIsAuthenticated();
  // Using 'md' to match App.tsx logic where it auto-closes below md
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const handleLogout = () => {
    isAuthenticated &&
      instance
        .logoutRedirect({
          postLogoutRedirectUri: process.env.AZURE_FRONT_POST_LOGOUT_REDIRECT_URI ?? "",
        })
        .catch((error) => {
          console.error(error);
          appInsightsTracker.trackException(`logout failed, cause : ${JSON.stringify(error)}`);
        });
  };

  const { sidebarBackgroundColor, buttonBackgroundColor } = computeBackgroundColor(theme.palette);

  const drawerContent = (
    <>
      <StyledDrawerHeader>
        <Button
          onClick={handleDrawerClose}
          sx={{
            width: "100%",
            borderRadius: 0,
            color: "black",
            backgroundColor: "white",
            padding: isMobile || isTablet ? "8px 8px" : "11px 8px",
            ":hover": {
              backgroundColor: "white",
            },
          }}
        >
          <FontAwesomeIcon icon="xmark" size="3x" color="red" />
        </Button>
      </StyledDrawerHeader>
      <List
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          paddingTop: isMobile || isTablet ? "0" : "16px",
        }}
      >
        <ListItem disablePadding>
          <ListItemButton selected={pathname === "/"} sx={() => getSelectedStyle(buttonBackgroundColor)}>
            <ListItemIcon>
              <Tooltip title={translate(RESOURCES_KEYS.MENU_HOME)} placement="right">
                <Icon sx={styledNavigation}>
                  <Link to="/">
                    <img src={Home} alt="home" />
                  </Link>
                </Icon>
              </Tooltip>
            </ListItemIcon>
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton selected={pathname === "/categories"} sx={() => getSelectedStyle(buttonBackgroundColor)}>
            <ListItemIcon>
              <Tooltip title={translate(RESOURCES_KEYS.MENU_CATEGORIES)} placement="right">
                <Icon sx={styledNavigation}>
                  <Link to="/categories">
                    <img src={Library} alt="library" />
                  </Link>
                </Icon>
              </Tooltip>
            </ListItemIcon>
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton selected={pathname === "/starred"} sx={() => getSelectedStyle(buttonBackgroundColor)}>
            <ListItemIcon>
              <Tooltip title={translate(RESOURCES_KEYS.MENU_FAVORITES)} placement="right">
                <Icon sx={styledNavigation}>
                  <Link to="/starred">
                    <img src={Favorite} alt="starred" />
                  </Link>
                </Icon>
              </Tooltip>
            </ListItemIcon>
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton selected={pathname === "/acknowledgement"} sx={() => getSelectedStyle(buttonBackgroundColor)}>
            <ListItemIcon>
              <Tooltip title={translate(RESOURCES_KEYS.MENU_ACKNOWLEDGEMENT)} placement="right">
                <Icon sx={styledNavigation}>
                  <Link to="/acknowledgement">
                    <img src={Acknowledgement} alt="acknowledgement" style={{ marginLeft: "4px" }} />
                  </Link>
                </Icon>
              </Tooltip>
            </ListItemIcon>
          </ListItemButton>
        </ListItem>
      </List>
      <List>
        <ListItem disablePadding>
          <ListItemButton selected={pathname === "/settings"} sx={() => getSelectedStyle(buttonBackgroundColor)}>
            <ListItemIcon sx={styledNavigationItemIcon}>
              <Tooltip title={translate(RESOURCES_KEYS.MENU_SETTINGS)} placement="right">
                <Icon sx={styledNavigation}>
                  <Link to="/settings">
                    <img src={Parameters} alt="settings" />
                  </Link>
                </Icon>
              </Tooltip>
            </ListItemIcon>
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton sx={() => getSelectedStyle(buttonBackgroundColor)}>
            <ListItemIcon>
              <Button onClick={handleLogout}>
                <Tooltip title={translate(RESOURCES_KEYS.MENU_LOGOUT)} placement="right">
                  <Icon sx={styledNavigation}>
                    <img src={Logout} alt="logout" />
                  </Icon>
                </Tooltip>
              </Button>
            </ListItemIcon>
          </ListItemButton>
        </ListItem>
        <Box component="span" className="App__version">
          v : {packageJson.version}{" "}
        </Box>
        <Box component="span" className="App__app-env">
          {process.env.REACT_APP_ENV}
        </Box>
      </List>
    </>
  );

  if (isMobile || isTablet) {
    return (
      <StyledSwipeableDrawer
        drawerwidth={drawerwidth}
        PaperProps={{
          sx: {
            backgroundColor: sidebarBackgroundColor,
            border: 0,
          },
        }}
        anchor="left"
        open={open}
        onClose={handleDrawerClose}
        onOpen={handleDrawerOpen || (() => {})}
        disableBackdropTransition={!iOS}
        disableDiscovery={iOS}
      >
        {drawerContent}
      </StyledSwipeableDrawer>
    );
  }

  return (
    <StyledDrawer
      drawerwidth={drawerwidth}
      PaperProps={{
        sx: {
          backgroundColor: sidebarBackgroundColor,
          border: 0,
        },
      }}
      variant="persistent"
      anchor="left"
      open={open}
    >
      {drawerContent}
    </StyledDrawer>
  );
};

const iOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);

export default Sidebar;

function computeBackgroundColor(palette: Palette): {
  sidebarBackgroundColor: any;
  buttonBackgroundColor: any;
} {
  let sidebarBackgroundColor = palette.primary.dark;
  let buttonBackgroundColor = {
    selected: palette.primary.main,
    hover: palette.primary.light,
  };
  if (process.env.REACT_APP_ENV === "DEV") {
    sidebarBackgroundColor = palette.error.dark;
    buttonBackgroundColor = {
      selected: palette.error.main,
      hover: palette.error.light,
    };
  } else if (process.env.REACT_APP_ENV === "TEST") {
    sidebarBackgroundColor = palette.success.dark;
    buttonBackgroundColor = {
      selected: palette.success.main,
      hover: palette.success.light,
    };
  }

  return { sidebarBackgroundColor, buttonBackgroundColor };
}
